import { Entity } from '@api/api/entity-schema';
import { DetailedInsight } from '@api/api/insights';
import { Icon } from '@design/components/icon/Icon';
import { formatPriceWithCurrency } from '@helpers/utils/currency/currency';
import { formatUtcDate } from '@terminal/services/insights/utils/insight-utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ScenarioInsightPill.module.scss';

export interface ScenarioInsightPillProps {
  insight: DetailedInsight;
  entity: Entity;
}

export const ScenarioInsightPill = ({
  insight,
  entity,
}: ScenarioInsightPillProps) => {
  const { t } = useTranslation(['widget', 'stream']);

  const metadataSentence = useMemo(() => {
    const lastValueDate = insight.metadata.last_value_date;

    if (!lastValueDate) {
      return null;
    }

    return t('widget:insights.scenarioPublishedMetadata', {
      publishedDate: formatUtcDate(insight.created_at, 'PP'),
      entityName: entity.name,
      price: formatPriceWithCurrency(
        insight.metadata.last_value,
        insight.metadata.currency
      ),
      referenceDate: formatUtcDate(lastValueDate, 'PP'),
    });
  }, [entity.name, insight, t]);

  if (!metadataSentence) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="scenario-insight-pill">
      <div className={styles.pillWrapper}>
        <div className={styles.iconWrapper}>
          <Icon
            className={styles.styledScenarioIcon}
            iconName="ExploreNav"
            size={12}
            fillColor="var(--icon-decorative-indigo)"
          />
        </div>
        <div className={styles.titleLabel}>{t('stream:pills.scenario')}</div>
      </div>
      <p className={styles.metadataText}>{metadataSentence}</p>
    </div>
  );
};
